// src/themes/lino/elements/Header/PrimaryDrawer.tsx
'use client';

import { ChevronRight, X } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';
import { normalizeLink } from '@/core/lib/utils';

type SidebarMenuProps = {
  mainItems: MenuItem[];
};

export default function PrimaryDrawer({ mainItems }: SidebarMenuProps) {
  const { primaryOpen, setPrimaryOpen, setSecondaryOpen, setSelectedItemId } =
    useDrawerStore();

  function handleClose() {
    setPrimaryOpen(false);
    setSecondaryOpen(false);
  }

  function handleItemClick(id: number) {
    setSelectedItemId(id);
    setSecondaryOpen(true);
  }

  return (
    <div
      className={`transition-transform no-scrollbar duration-300 ease-in-out bg-primary-0 overflow-y-auto pointer-events-auto fixed top-0 h-full z-50 lg:z-70 ${primaryOpen
        ? 'translate-x-0 w-full lg:w-80 ltr:border-r rtl:border-l border-primary-200'
        : 'ltr:-translate-x-full rtl:translate-x-full w-0 lg:w-80'
        } ltr:left-0 rtl:right-0`}
      role="navigation"
      aria-label="Primary navigation drawer"
    >
      <div className="p-6 min-h-full">
        <div className="flex justify-end items-center mb-8 pb-4 border-b border-primary-100">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close drawer"
            className="rounded-full hover:bg-primary-100 text-primary-900"
          >
            <X className="w-5 h-5 stroke-[1.5]" />
          </Button>
        </div>

        <div>
          {mainItems.length > 0 && (
            <ul className="flex flex-col">
              {mainItems.map(item => (
                <li
                  key={item.id}
                  className="group relative border-b border-primary-100 last:border-0"
                  onMouseEnter={() => {
                    if (window.innerWidth >= 1024 && item.children?.length > 0) {
                      handleItemClick(item.id);
                    }
                  }}
                >
                  <div className="flex items-center justify-between cursor-pointer min-h-13 group-hover:bg-primary-50 rounded-sm px-2">
                    <Link
                      href={normalizeLink(item.link) || '#'}
                      className="flex-1 py-3"
                      onClick={() => handleClose()}
                    >
                      <span className="text-[13px] tracking-wide text-primary-800">{item.title}</span>
                    </Link>

                    {item.children && item.children.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleItemClick(item.id)}
                        aria-label={`Open submenu for ${item.title}`}
                        className="rounded-full hover:bg-primary-100"
                        aria-expanded={
                          useDrawerStore.getState().secondaryOpen &&
                          useDrawerStore.getState().selectedItemId === item.id
                        }
                      >
                        <ChevronRight className="w-4 h-4 stroke-[1.5] rtl:rotate-180 text-primary-500" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}